import { useEffect, useState } from "react";
import { ClipboardList, Loader2, Link2, Copy, ChevronDown, ChevronUp, Plus, Pencil, X, Check, Mail, ExternalLink, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import {
  formsList, formSubmissions, formCreateSubmission, formsSources, formCreate, formUpdate, formSubmissionResult, formEmailResult, formSendInvite,
  fetchAdminUsers, fetchMyTeam, fetchCoupons, designListTemplates, isAdminUser,
} from "@/lib/api";

const TYPE_TO_SOURCE = { Protocol: "protocol_focus", Services: "spa_services" };
const SOURCE_TO_TYPE = { protocol_focus: "Protocol", spa_services: "Services" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const defaultsFor = (type) => type === "Services"
  ? { title: "Which services are you interested in?", question_text: "Which services are you interested in?", submit_text: "Send my picks", confirmation_title: "Thank you!", confirmation_text: "We'll reach out shortly to schedule your visit." }
  : { title: "Find Your Personalized Protocol", question_text: "What would you like help with?", submit_text: "Get My Recommendations", confirmation_title: "Your Protocol Is Ready", confirmation_text: "Based on your answers, here is the OreVital protocol we recommend.",
      intro_text: "Discover what’s possible with OreVital Performance Peptides. Our non-invasive transdermal formulations bring performance and wellness peptides into a simple topical routine—no needles or injections.\nSelect your goals and concerns below, and we’ll build your personalized OreVital Peptide Protocol for recovery, performance, skin, hair and overall wellness." };

// Step 1 type → Step 2 pick items visually → save to the Airtable Forms row.
function FormBuilder({ userRecordId, form, onClose, onSaved }) {
  const [src, setSrc] = useState(null);
  const initialType = form ? (form.service_ids?.length && !form.focus_ids?.length ? "Services" : "Protocol") : "Protocol";
  const [type, setType] = useState(initialType);
  const [v, setV] = useState(() => form
    ? { name: form.name, title: form.title, intro_text: form.intro_text, question_text: form.question_text, submit_text: form.submit_text, confirmation_title: form.confirmation_title, confirmation_text: form.confirmation_text, active: form.active, picks: initialType === "Services" ? form.service_ids || [] : form.focus_ids || [] }
    : { name: "", active: true, picks: [], ...defaultsFor("Protocol") });
  const [saving, setSaving] = useState(false);
  const [more, setMore] = useState(!!form);
  useEffect(() => { formsSources(userRecordId).then(setSrc).catch(() => setSrc({ sources: [] })); }, [userRecordId]);
  const source = src?.sources?.find((s) => s.key === TYPE_TO_SOURCE[type]);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const pickType = (t) => { setType(t); setV((s) => ({ ...s, picks: [], ...(form ? {} : defaultsFor(t)) })); };
  const toggle = (id) => setV((s) => ({ ...s, picks: s.picks.includes(id) ? s.picks.filter((x) => x !== id) : [...s.picks, id] }));

  async function save() {
    if (!v.name.trim()) { toast.error("Give the form a name"); return; }
    if (!v.picks.length) { toast.error(`Pick at least one ${type === "Services" ? "service" : "protocol focus"}`); return; }
    setSaving(true);
    const payload = { user_record_id: userRecordId, name: v.name, type: type === "Services" ? "Service" : "Protocol", title: v.title, intro_text: v.intro_text, question_text: v.question_text,
      submit_text: v.submit_text, confirmation_title: v.confirmation_title, confirmation_text: v.confirmation_text, active: v.active,
      protocol_focus: type === "Protocol" ? v.picks : [], spa_services: type === "Services" ? v.picks : [] };
    try {
      const r = form ? await formUpdate(form.id, payload) : await formCreate(payload);
      toast.success(form ? "Form updated" : `Form ${r.form.form_id} created`);
      onSaved(r.form);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save form");
    } finally { setSaving(false); }
  }

  const field = (label, key, Comp = Input, props = {}) => (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
      <Comp value={v[key] || ""} onChange={set(key)} className="mt-1 rounded-lg text-sm" data-testid={`fb-${key}`} {...props} />
    </label>
  );
  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4 space-y-4" data-testid="form-builder">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-zinc-900">{form ? `Edit ${form.form_id}` : "New form"}</h3>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="fb-close"><X size={16} /></button>
      </div>
      {field("Form name", "name", Input, { placeholder: type === "Services" ? "Spring service menu" : "Peptide Protocol Consultation" })}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">1 · Form type</div>
        <div className="inline-flex rounded-full border border-zinc-200 bg-white p-0.5" data-testid="fb-type">
          {["Protocol", "Services"].map((t) => (
            <button key={t} type="button" onClick={() => pickType(t)} aria-pressed={type === t}
              className={`px-4 h-8 rounded-full text-sm font-semibold transition-colors ${type === t ? "bg-[#0b2545] text-white" : "text-zinc-600 hover:bg-zinc-100"}`} data-testid={`fb-type-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>
      </div>
      <div data-testid={`fb-source-${TYPE_TO_SOURCE[type]}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">2 · Items on the form {source ? `(${source.label})` : ""}</div>
          <span className="text-[11px] font-semibold text-violet-700" data-testid="fb-pick-count">{v.picks.length} selected</span>
        </div>
        {!src ? <Loader2 className="animate-spin text-zinc-400" size={14} /> : !source || source.options.length === 0 ? (
          <p className="text-xs text-zinc-500 bg-white rounded-xl border border-zinc-200 p-3">{type === "Services" ? "No services on your account yet — add them under Folio → Services first." : "No protocol focuses available."}</p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
            {source.options.map((o) => {
              const on = v.picks.includes(o.id);
              return (
                <li key={o.id}>
                  <button type="button" onClick={() => toggle(o.id)} aria-pressed={on}
                    className={`w-full text-left rounded-xl border bg-white overflow-hidden transition-[border-color,box-shadow] ${on ? "border-[#b88a2b] shadow-sm ring-1 ring-[#b88a2b]" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={`fb-opt-${o.id}`}>
                    <div className="relative h-20 bg-[#f6f4ef]">
                      {o.image_url ? <img src={o.image_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-[#0b2545]/40 text-xs font-semibold">{o.category || type}</div>}
                      <span className={`absolute top-1.5 right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full border ${on ? "bg-[#b88a2b] border-[#b88a2b] text-white" : "bg-white/90 border-zinc-300 text-transparent"}`}><Check size={12} strokeWidth={3} /></span>
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-semibold text-[#0b2545] leading-snug line-clamp-2">{o.label}</div>
                      {(o.price != null || o.duration) && <div className="text-[10px] text-zinc-500 mt-0.5">{[o.price != null ? `$${o.price}` : "", o.duration ? `${o.duration} min` : ""].filter(Boolean).join(" · ")}</div>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button type="button" onClick={() => setMore((m) => !m)} className="text-xs font-semibold text-violet-700 inline-flex items-center gap-1" data-testid="fb-more">
        {more ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Wording (question, button, confirmation)
      </button>
      {more && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{field("Title", "title")}{field("Question text", "question_text")}</div>
          {field("Intro text", "intro_text", Textarea, { rows: 5 })}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{field("Submit button", "submit_text")}{field("Confirmation title", "confirmation_title")}</div>
          {field("Confirmation text", "confirmation_text", Textarea, { rows: 2 })}
          <label className="flex items-center gap-2 text-sm text-zinc-700"><input type="checkbox" checked={!!v.active} onChange={(e) => setV((s) => ({ ...s, active: e.target.checked }))} data-testid="fb-active" /> Active</label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" className="rounded-lg" onClick={onClose} data-testid="fb-cancel">Cancel</Button>
        <Button disabled={saving} onClick={save} className="rounded-lg bg-[#0b2545] hover:bg-[#143462] text-white" data-testid="fb-save">
          {saving ? <Loader2 className="animate-spin" size={14} /> : form ? "Save changes" : "Create form"}
        </Button>
      </div>
    </div>
  );
}

// Recipient picker — same CRM/team search as the flyer email dialog.
function RecipientPicker({ userRecordId, user, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        let list;
        if (isAdminUser(user)) {
          const res = await fetchAdminUsers({ callerUserRecordId: userRecordId, q, limit: 8 });
          list = res?.users || [];
        } else {
          let tm = team;
          if (!tm) { const res = await fetchMyTeam(user); tm = (res?.team_members || []).map((m) => ({ ...m, id: m.contact_record_id || m.id })); if (alive) setTeam(tm); }
          const n = q.trim().toLowerCase();
          list = tm.filter((m) => !n || `${m.full_name || ""} ${m.email || ""}`.toLowerCase().includes(n)).slice(0, 8);
        }
        if (alive) setResults(list.filter((u) => u.email));
      } catch { if (alive) setResults([]); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, user, userRecordId, team]);
  return (
    <div className="space-y-2" data-testid="form-recipient-picker">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a customer by name or email" className="h-9 pl-8 rounded-lg text-sm" data-testid="form-recipient-search" />
      </div>
      {results.length > 0 && (
        <ul className="rounded-xl border border-zinc-200 bg-white divide-y divide-zinc-100 max-h-48 overflow-y-auto">
          {results.map((u) => (
            <li key={u.id}>
              <button type="button" onClick={() => onPick(u)} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex justify-between gap-2" data-testid={`form-recipient-${u.id}`}>
                <span className="font-medium text-zinc-800 truncate">{u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}</span>
                <span className="text-zinc-500 truncate">{u.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultPanel({ sub, userRecordId, userId }) {
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponId, setCouponId] = useState("");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  useEffect(() => {
    formSubmissionResult(sub.id, userRecordId).then(setData).catch(() => setData({ error: true }));
    if (userId) fetchCoupons(userId).then((r) => setCoupons(r?.coupons || [])).catch(() => setCoupons([]));
  }, [sub.id, userRecordId, userId]);
  async function email() {
    setSending(true);
    try {
      const r = await formEmailResult(sub.id, { user_record_id: userRecordId, coupon_id: couponId || undefined });
      if (r.sent) { toast.success(`Result emailed to ${sub.email || "the customer"}`); setData((d) => ({ ...d, result: { ...d.result, emailed_at: new Date().toISOString() } })); }
      else toast.error(r.error || r.detail || "Email was not sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setSending(false); }
  }
  if (!data) return <Loader2 className="animate-spin text-zinc-400" size={14} />;
  if (data.error || !data.result) return <p className="text-xs text-zinc-500">No result generated for this submission.</p>;
  const r = data.result;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2 text-xs" data-testid={`form-result-${sub.id}`}>
      <div className="font-semibold text-emerald-900">{r.title || "Result"} {r.emailed_at && <span className="ml-2 font-normal text-emerald-700">· emailed {fmt(r.emailed_at)}</span>}</div>
      {r.focus_names?.length > 0 && <div><span className="text-zinc-500">Chose:</span> {r.focus_names.join(", ")}</div>}
      {r.service_names?.length > 0 && <div><span className="text-zinc-500">Services:</span> {r.service_names.join(", ")}</div>}
      {r.product_names?.length > 0 && <div><span className="text-zinc-500">Recommended:</span> {r.product_names.join(", ")}</div>}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {r.result_url && <a href={r.result_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-white border border-zinc-200 px-2.5 py-1 font-semibold text-[#0b2545] hover:bg-zinc-50" data-testid={`form-result-open-${sub.id}`}><ExternalLink size={11} /> Open result page</a>}
        {data.preview_html && <button type="button" onClick={() => setShowPreview((s) => !s)} className="inline-flex items-center gap-1 rounded-full bg-white border border-zinc-200 px-2.5 py-1 font-semibold text-[#0b2545] hover:bg-zinc-50" data-testid={`form-result-preview-${sub.id}`}><Eye size={11} /> {showPreview ? "Hide" : "Preview"} email</button>}
        {coupons.length > 0 && (
          <select value={couponId} onChange={(e) => setCouponId(e.target.value)} className="h-7 rounded-full border border-zinc-200 bg-white px-2 text-xs" data-testid={`form-result-coupon-${sub.id}`}>
            <option value="">No coupon</option>
            {coupons.map((c) => <option key={c.id} value={c.id}>{c.coupon_name}{c.coupon_tier ? ` · ${c.coupon_tier}` : ""}</option>)}
          </select>
        )}
        <Button size="sm" disabled={sending} onClick={email} className="h-7 rounded-full bg-[#0b2545] hover:bg-[#143462] text-white px-3 text-xs" data-testid={`form-result-email-${sub.id}`}>
          {sending ? <Loader2 className="animate-spin" size={12} /> : <><Mail size={11} className="mr-1" /> {r.emailed_at ? "Email again" : "Email result"}</>}
        </Button>
      </div>
      {showPreview && <iframe title="result-preview" sandbox="" srcDoc={`<!doctype html><html><body style="margin:0;background:#f6f4ef">${data.preview_html}</body></html>`} className="w-full h-[420px] rounded-lg border border-zinc-200 bg-white" data-testid={`form-result-frame-${sub.id}`} />}
    </div>
  );
}

function SubmissionRow({ s, publicBase, userRecordId, userId }) {
  const [open, setOpen] = useState(false);
  const url = s.token ? `${publicBase}/form/${s.token}` : "";
  const submitted = s.status === "Submitted";
  return (
    <li className="py-2 text-xs border-t border-zinc-100" data-testid={`form-sub-${s.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-zinc-400 w-16">{s.submission_id || "—"}</span>
        <span className="font-medium text-zinc-800 flex-1 min-w-[120px]">{[s.first_name, s.last_name].filter(Boolean).join(" ") || s.email || "No recipient"}</span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${submitted ? "bg-emerald-50 text-emerald-700" : s.status === "Opened" ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-600"}`} data-testid={`form-sub-status-${s.id}`}>{s.status || "—"}</span>
        <span className="text-zinc-500">{submitted ? fmt(s.submitted_at) : s.status === "Opened" ? fmt(s.opened_at) : fmt(s.sent_at)}</span>
        {url && <button type="button" className="inline-flex items-center gap-1 text-violet-700 hover:underline" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} data-testid={`form-sub-copy-${s.id}`}><Copy size={11} /> Copy link</button>}
        {submitted && <button type="button" className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline" onClick={() => setOpen((o) => !o)} data-testid={`form-sub-result-${s.id}`}><Eye size={11} /> {open ? "Hide result" : "View result"}</button>}
      </div>
      {open && <div className="mt-2"><ResultPanel sub={s} userRecordId={userRecordId} userId={userId} /></div>}
    </li>
  );
}

function FormCard({ form, userRecordId, user, publicBase, onEdit }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [couponId, setCouponId] = useState("");
  async function load() { try { setSubs((await formSubmissions(form.id, userRecordId)).submissions); } catch { setSubs([]); } }
  useEffect(() => {
    if (!open || subs !== null) return;
    load();
    designListTemplates(userRecordId).then((r) => setTemplates(r?.items || r?.templates || [])).catch(() => setTemplates([]));
    const uid = user?.user_id || user?.id;
    if (uid) fetchCoupons(uid).then((r) => setCoupons(r?.coupons || [])).catch(() => setCoupons([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  async function sendInvite() {
    if (!recipient) return;
    setSending(true);
    try {
      const r = await formSendInvite(form.id, { user_record_id: userRecordId, template_id: templateId || undefined, coupon_id: couponId || undefined,
        recipients: [{ id: recipient.id, email: recipient.email, first_name: recipient.first_name || "", last_name: recipient.last_name || "", full_name: recipient.full_name || "" }] });
      if (r.sent) { toast.success(`Form emailed to ${recipient.email}`); setRecipient(null); load(); }
      else toast.error(r.error || r.detail || "Email was not sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setSending(false); }
  }
  async function createLink() {
    if (!recipient) return;
    setCreating(true);
    try {
      const r = await formCreateSubmission(form.id, { user_record_id: userRecordId, contact_user_record_id: recipient.id, email: recipient.email, source: "Manual" });
      await navigator.clipboard.writeText(r.url).catch(() => {});
      toast.success(`Form link for ${recipient.full_name || recipient.email} created & copied`, { description: "Paste it anywhere, or send the form via Marketing AI → Emails with the {{FORM_EMBED}} token.", duration: 9000 });
      setRecipient(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link"); } finally { setCreating(false); }
  }
  const kind = form.service_count && !form.focus_count ? "Services" : "Protocol";
  const items = kind === "Services" ? `${form.service_count} services` : `${form.focus_count} protocol focus`;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={`form-card-${form.id}`} data-form-id={form.form_id}>
      <button type="button" className="w-full flex items-start justify-between gap-3 text-left" onClick={() => setOpen((o) => !o)} data-testid={`form-card-toggle-${form.id}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-zinc-400">{form.form_id}</span>
            <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${form.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{form.active ? "Active" : "Inactive"}</span>
            <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-violet-50 text-violet-700">{kind}</span>
          </div>
          <div className="font-semibold text-zinc-900 mt-1">{form.name}</div>
          <div className="text-xs text-zinc-500 truncate">{form.title} · {items}</div>
        </div>
        {open ? <ChevronUp size={16} className="text-zinc-400 shrink-0 mt-1" /> : <ChevronDown size={16} className="text-zinc-400 shrink-0 mt-1" />}
      </button>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        <button type="button" onClick={() => onEdit(form)} className="inline-flex items-center gap-1 text-violet-700 hover:underline" data-testid={`form-edit-${form.id}`}><Pencil size={11} /> Edit</button>
        <span className="text-zinc-300">·</span>
        <span className="text-zinc-500">Email token:</span>
        <button type="button" onClick={() => navigator.clipboard.writeText(`{{FORM_EMBED:${form.form_id}}}`).then(() => toast.success("Token copied — paste it into an email in Marketing AI → Emails"))} className="font-mono text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded px-1.5 py-0.5" data-testid={`form-token-FORM_EMBED-${form.id}`}>{`{{FORM_EMBED:${form.form_id}}}`}</button>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3 space-y-2" data-testid={`form-newlink-${form.id}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 flex items-center gap-1"><Link2 size={12} /> Send to a customer</div>
            {recipient ? (
              <div className="space-y-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white border border-zinc-200 px-2.5 py-1 font-medium text-zinc-800" data-testid={`form-recipient-chosen-${form.id}`}>{recipient.full_name || recipient.email} · {recipient.email}</span>
                  <button type="button" onClick={() => setRecipient(null)} className="text-zinc-500 hover:underline">change</button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="h-8 rounded-lg border border-zinc-200 bg-white px-2 text-xs max-w-[220px]" data-testid={`form-invite-template-${form.id}`}>
                    <option value="">Default invite (form embedded)</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.title || t.slug}</option>)}
                  </select>
                  <select value={couponId} onChange={(e) => setCouponId(e.target.value)} className="h-8 rounded-lg border border-zinc-200 bg-white px-2 text-xs" data-testid={`form-invite-coupon-${form.id}`}>
                    <option value="">No coupon</option>
                    {coupons.map((c) => <option key={c.id} value={c.id}>{c.coupon_name}{c.coupon_tier ? ` · ${c.coupon_tier}` : ""}</option>)}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" disabled={sending || creating} onClick={sendInvite} className="h-8 rounded-lg bg-[#0b2545] hover:bg-[#143462] text-white" data-testid={`form-invite-send-${form.id}`}>
                    {sending ? <Loader2 className="animate-spin" size={14} /> : <><Mail size={14} className="mr-1" /> Send form by email</>}
                  </Button>
                  <Button size="sm" variant="outline" disabled={creating || sending} onClick={createLink} className="h-8 rounded-lg" data-testid={`form-newlink-create-${form.id}`}>
                    {creating ? <Loader2 className="animate-spin" size={14} /> : <><Link2 size={14} className="mr-1" /> Just copy a link</>}
                  </Button>
                </div>
              </div>
            ) : <RecipientPicker userRecordId={userRecordId} user={user} onPick={setRecipient} />}
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 mb-1">Submissions</div>
            {subs === null ? <Loader2 className="animate-spin text-zinc-400" size={14} /> : subs.length === 0 ? <p className="text-xs text-zinc-500">No submissions yet.</p> : (
              <ul data-testid={`form-subs-${form.id}`}>{subs.map((s) => <SubmissionRow key={s.id} s={s} publicBase={publicBase} userRecordId={userRecordId} userId={user?.user_id || user?.id} />)}</ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FormsTab({ userRecordId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [builder, setBuilder] = useState(null); // null | "new" | form
  const load = () => formsList(userRecordId).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Could not load forms"));
  useEffect(() => { load(); }, [userRecordId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><ClipboardList size={18} /> Forms</h2>
            <p className="text-sm text-white/85 mt-1">Pick a type, choose the items, send a personal link. When the customer answers, their personalized protocol is built and emailed automatically — just like a flyer.</p>
          </div>
          <Button size="sm" onClick={() => setBuilder("new")} className="shrink-0 rounded-lg bg-white text-violet-700 hover:bg-violet-50" data-testid="forms-new"><Plus size={14} className="mr-1" /> New form</Button>
        </div>
      </div>
      {builder && <FormBuilder userRecordId={userRecordId} form={builder === "new" ? null : builder} onClose={() => setBuilder(null)} onSaved={() => { setBuilder(null); load(); }} />}
      {err ? <p className="text-sm text-rose-600" data-testid="forms-error">{err}</p>
        : !data ? <Loader2 className="animate-spin text-zinc-400" size={18} />
        : data.forms.length === 0 ? <p className="text-sm text-zinc-500" data-testid="forms-empty">No forms yet — create your first one.</p>
        : data.forms.map((f) => <FormCard key={f.id} form={f} userRecordId={userRecordId} user={user} publicBase={data.public_base} onEdit={setBuilder} />)}
    </div>
  );
}
